/**
 * RequireSubscription
 *
 * Hard-block gate rendered inside the protected route tree. If the signed-in
 * user lacks an active/trialing subscription, the app is replaced by the
 * <Paywall/>. Otherwise children render normally.
 *
 * Controlled by the VITE_STRIPE_ENABLED kill-switch: when not explicitly
 * 'true', this is a pass-through (no status request, no paywall) so the app
 * behaves exactly as before billing existed. The backend `billing_enabled`
 * flag is also honored as a second safety net.
 *
 * Post-checkout grace: when the user returns from Stripe Checkout
 * (?checkout=success), the subscription webhook may not have landed yet. During
 * a short window we poll the status and show an "activating" screen instead of
 * flashing the paywall, so the webhook race is invisible.
 */

import { useRef, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBillingStatus } from '@/hooks/queries';
import { hasAccess, freePassActive } from '@/services/billing';
import { Paywall } from './Paywall';

const BILLING_ENABLED = import.meta.env.VITE_STRIPE_ENABLED === 'true';

// Poll cadence + how long to keep polling after returning from checkout.
const POLL_MS = 2000;
const GRACE_MS = 20_000;

const F = "'DM Sans', sans-serif";

function ActivatingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#FAF9F7' }}>
      <div className="text-center">
        <div
          className="mx-auto mb-5 rounded-full animate-spin"
          style={{ width: 36, height: 36, border: '3px solid #E8E6E1', borderTopColor: '#3ECDC6' }}
        />
        <h1 style={{ fontFamily: F, fontWeight: 600, fontSize: 20, color: '#2A2926' }}>
          Activating your subscription…
        </h1>
        <p style={{ fontFamily: F, fontSize: 14, color: '#6B675E', marginTop: 6 }}>
          This only takes a moment.
        </p>
      </div>
    </div>
  );
}

/**
 * Shown when activation is taking longer than the grace window. The payment
 * succeeded; the confirmation just hasn't reached us yet. Give the user a way
 * to re-check rather than silently dropping them onto the paywall.
 */
function TakingLongerScreen({ onRecheck, busy }: { onRecheck: () => void; busy: boolean }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#FAF9F7' }}>
      <div className="text-center max-w-sm">
        <h1 style={{ fontFamily: F, fontWeight: 600, fontSize: 20, color: '#2A2926' }}>
          Almost there
        </h1>
        <p style={{ fontFamily: F, fontSize: 14, color: '#6B675E', marginTop: 8, lineHeight: 1.5 }}>
          Your payment went through — we’re just waiting on the confirmation from
          Stripe. This usually lands within a few seconds.
        </p>
        <button
          onClick={onRecheck}
          disabled={busy}
          className="mt-6 px-6 py-2.5 rounded-full text-sm font-semibold text-white transition hover:-translate-y-0.5 disabled:opacity-50"
          style={{ background: '#3ECDC6', fontFamily: F }}
        >
          {busy ? 'Checking…' : 'Check again'}
        </button>
      </div>
    </div>
  );
}

export function RequireSubscription({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const justCheckedOut = searchParams.get('checkout') === 'success';

  // Start (and clear) the polling deadline based on the checkout return.
  const deadlineRef = useRef<number | null>(null);
  if (justCheckedOut && deadlineRef.current === null) {
    deadlineRef.current = Date.now() + GRACE_MS;
  } else if (!justCheckedOut) {
    deadlineRef.current = null;
  }

  const { data, isLoading, isError, isFetching, refetch } = useBillingStatus(BILLING_ENABLED, {
    refetchInterval: (query) => {
      if (!justCheckedOut) return false;
      const d = query.state.data;
      if (d && (hasAccess(d.status) || freePassActive(d.free_pass_until))) return false; // activated — stop polling
      if (deadlineRef.current && Date.now() > deadlineRef.current) return false; // gave up
      return POLL_MS;
    },
  });

  // Kill-switch off → pass through entirely.
  if (!BILLING_ENABLED) {
    return <>{children}</>;
  }

  const access =
    !!data && (!data.billing_enabled || freePassActive(data.free_pass_until) || hasAccess(data.status));
  if (access) {
    return <>{children}</>;
  }

  // Returning from checkout: show activation UI rather than the paywall — this
  // user just paid, so the paywall would be confusing.
  if (justCheckedOut) {
    const withinGrace = deadlineRef.current !== null && Date.now() <= deadlineRef.current;
    if (withinGrace || isLoading) {
      return <ActivatingScreen />;
    }
    // Grace elapsed without the webhook landing — offer a manual re-check.
    return <TakingLongerScreen onRecheck={() => refetch()} busy={isFetching} />;
  }

  // Don't flash the paywall while the first status check is in flight.
  if (isLoading) {
    return null;
  }

  // On a transient status error, fail open rather than locking a paying user out.
  if (isError || !data) {
    return <>{children}</>;
  }

  return <Paywall status={data.status} />;
}

export default RequireSubscription;
